import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Mail, Sparkles, UserPlus, Monitor, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/ThemeToggle";

export const Route = createFileRoute("/device-limit")({
  head: () => ({
    meta: [
      { title: "Device limit reached — Lumvio" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "This Lumvio account is locked to its 3 registered devices. Contact support to reset your devices.",
      },
    ],
  }),
  component: DeviceLimitPage,
});

type DeviceRow = {
  id: string;
  device_name: string | null;
  ip_address: string | null;
  location: string | null;
  last_seen_at: string;
  is_active: boolean;
};

function DeviceLimitPage() {
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        // Not signed in — nothing to show, but still show the informative screen.
        if (!cancelled) setLoading(false);
        return;
      }
      const { data } = await supabase.rpc("get_my_devices");
      if (cancelled) return;
      const list = ((data ?? []) as DeviceRow[]).filter((d) => d.is_active);
      setDevices(list);
      setLoading(false);
      // Ensure the informative screen is clean of an active session.
      await supabase.auth.signOut();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_600px_at_15%_10%,color-mix(in_oklab,var(--primary)_20%,transparent),transparent_60%),radial-gradient(900px_500px_at_85%_90%,color-mix(in_oklab,var(--primary)_16%,transparent),transparent_60%)]" />
      </div>

      <div className="relative z-10 flex items-center justify-between px-5 py-5 sm:px-10">
        <Link to="/" className="inline-flex items-center gap-2">
          <BrandLogo size="sm" />
        </Link>
        <ThemeToggle />
      </div>

      <main className="relative z-10 mx-auto flex max-w-2xl flex-col items-center px-5 pb-16 pt-6 text-center sm:px-8">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
          className="relative"
        >
          <div className="absolute inset-0 rounded-full bg-primary/10 blur-3xl" aria-hidden />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
            <ShieldCheck className="h-10 w-10" strokeWidth={1.5} />
          </div>
        </motion.div>

        <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
          <Sparkles className="h-3 w-3" /> Account locked
        </span>

        <h1 className="mt-4 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
          Device limit reached
        </h1>
        <p className="mt-4 max-w-xl text-balance text-sm leading-relaxed text-muted-foreground sm:text-base">
          This account is locked to your <span className="font-semibold text-foreground">3 registered devices</span>. If you have upgraded your phone, bought a new computer, or lost access to an old device, please contact our support team at{" "}
          <a href="mailto:contact@lumvio.co" className="font-medium text-primary hover:underline">
            contact@lumvio.co
          </a>{" "}
          to reset your registered devices.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="h-11 rounded-full px-6">
            <a href="mailto:contact@lumvio.co?subject=Reset%20my%20devices">
              <Mail className="me-2 h-4 w-4" /> Contact support
            </a>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-11 rounded-full px-6">
            <Link to="/plans">View plans</Link>
          </Button>
          <Button asChild variant="ghost" size="lg" className="h-11 rounded-full px-6">
            <Link to="/auth" search={{ tab: "signup" } as never}>
              <UserPlus className="me-2 h-4 w-4" /> Create a new account
            </Link>
          </Button>
        </div>

        <div className="mt-12 w-full">
          <div className="mb-3 flex items-center justify-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            <Monitor className="h-3 w-3" /> Locked devices on this account
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : devices && devices.length > 0 ? (
            <ul className="grid gap-2 sm:grid-cols-3">
              {devices.map((d) => (
                <li
                  key={d.id}
                  className="rounded-2xl border border-border/60 bg-card/70 p-4 text-start backdrop-blur-sm"
                >
                  <div className="truncate text-sm font-medium">
                    {d.device_name ?? "Unknown device"}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Last active {new Date(d.last_seen_at).toLocaleDateString()}
                  </div>
                  {d.location && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{d.location}</div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-xs text-muted-foreground">
              We couldn't display your devices without an active session. Please contact support.
            </p>
          )}
        </div>

        <p className="mt-10 max-w-md text-[11px] leading-relaxed text-muted-foreground/70">
          For security and to prevent account sharing, device changes are handled by our support team only.
        </p>
      </main>
    </div>
  );
}
